import {RefObject, useEffect, useRef, useState} from 'react';
import type {ProviderRostersStatus} from '../../lib/stores/roster-store';
import {useScreenReaderAnnouncement} from '../comparison-empty-state/useScreenReaderAnnouncement';

type RosterListRetry<T extends HTMLElement> = {
    retry: () => void;
    // Where focus goes on retry, as the Try again button that had it gives way to a skeleton.
    // Attach it to the view's heading or label, with tabIndex={-1}
    focusRef: RefObject<T>;
};

/**
 * Try again for a view showing the roster list's failure: loads the list again, says "Retrying."
 * while that load is in flight, and keeps focus in the view
 *
 * @param status - The roster list's status, from useProviderRosterList
 * @param reload - Its reload
 */
export function useRosterListRetry<T extends HTMLElement = HTMLElement>(
    status: ProviderRostersStatus,
    reload: () => void,
): RosterListRetry<T> {
    const focusRef = useRef<T>(null);
    const [retrying, setRetrying] = useState(false);

    // A retry ends with its request. Clearing the message then is what lets the next retry,
    // with the same message, be announced again
    useEffect(() => {
        if (status !== 'loading') {
            setRetrying(false);
        }
    }, [status]);

    useScreenReaderAnnouncement(retrying ? 'Retrying.' : '');

    const retry = () => {
        setRetrying(true);
        focusRef.current?.focus();
        reload();
    };

    return {retry, focusRef};
}
